import React from 'react';
import { format, parseISO } from 'date-fns';
import CompactRangePicker from '../../CompactRangePicker';

/**
 * TimeEntries Component
 * Manages the scheduling section of an assignment, including single/multi-day entries,
 * time pickers, and the 'undated' toggle.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isUndated - Whether the assignment is undated
 * @param {Function} props.setIsUndated - Toggles undated mode
 * @param {boolean} props.isReadOnly - Whether the component is in view-only mode
 * @param {Array} props.entries - List of date/time entries
 * @param {Function} props.addEntry - Adds a new entry row
 * @param {Function} props.removeEntry - Removes an entry row
 * @param {Function} props.updateEntry - Updates a specific field in an entry row
 * @param {string|null} props.activePickerId - ID of the currently open date picker
 * @param {Function} props.setActivePickerId - Sets which date picker is open
 * @param {Function} props.setEntries - Updates the entire entries array
 */
const TimeEntries = ({
    isUndated,
    isReadOnly,
    entries,
    addEntry,
    removeEntry,
    updateEntry,
    activePickerId,
    setActivePickerId,
    setEntries
}) => {
    return (
        <div className="time-entries-wrapper">
            <div className="section-header" style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>🕒 Zeiteinträge</span>
                {!isUndated && !isReadOnly && (
                    <button 
                        type="button" 
                        className="btn-add-term" 
                        onClick={addEntry}
                        style={{ marginLeft: 'auto' }}
                    >
                        + Weiteren Termin
                    </button>
                )}
            </div>

            {!isUndated ? (
                entries.map((entry) => {
                     const isPickerOpen = activePickerId === entry.id;
                     
                     const getDisplayDate = () => {
                         if (!entry.startDate) return "Datum wählen";
                         const start = format(parseISO(entry.startDate), 'dd.MM.yy');
                         if (!entry.endDate || entry.endDate === entry.startDate) return start;
                         const end = format(parseISO(entry.endDate), 'dd.MM.yy');
                         return `${start} - ${end}`;
                     };

                     return (
                        <div key={entry.id} className="entry-row" style={{ zIndex: isPickerOpen ? 100 : 1 }}>
                            <div 
                                className={`assignment-date-pill ${isPickerOpen ? 'is-active' : ''}`}
                                onClick={() => !isReadOnly && setActivePickerId(isPickerOpen ? null : entry.id)}
                            >
                                <span className="date-pill-icon">📅</span>
                                <span className="date-pill-value">{getDisplayDate()}</span>
                                {entry.startDate && entry.endDate && entry.startDate !== entry.endDate && (
                                    <span className="date-pill-multiday-hint">mehrtägig</span>
                                )}

                                {isPickerOpen && (
                                    <div 
                                        className="range-picker-popover" 
                                        style={{ position: 'absolute', top: '110%', left: 0 }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <CompactRangePicker 
                                            startDate={entry.startDate}
                                            endDate={entry.endDate}
                                            onSelect={(start, end) => {
                                                updateEntry(entry.id, 'startDate', start);
                                                updateEntry(entry.id, 'endDate', end || start);
                                                updateEntry(entry.id, 'isMultiDay', !!end && start !== end);
                                                if (end) setActivePickerId(null);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="time-controls">
                                <label className="allday-label">
                                    <input
                                        type="checkbox"
                                        checked={!entry.withTime}
                                        disabled={isReadOnly}
                                        onChange={e => updateEntry(entry.id, 'withTime', !e.target.checked)}
                                    />
                                    Ganztag
                                </label>
                                {entry.withTime && (
                                    <div className="time-inputs">
                                        <input 
                                            type="time" 
                                            value={entry.startTime} 
                                            disabled={isReadOnly} 
                                            onChange={e => updateEntry(entry.id, 'startTime', e.target.value)} 
                                            onClick={e => !isReadOnly && e.target.showPicker?.()}
                                        />
                                        <span style={{ fontSize: '12px', opacity: 0.5 }}>-</span>
                                        <input 
                                            type="time" 
                                            value={entry.endTime} 
                                            disabled={isReadOnly} 
                                            onChange={e => updateEntry(entry.id, 'endTime', e.target.value)} 
                                            onClick={e => !isReadOnly && e.target.showPicker?.()}
                                        />
                                    </div>
                                )}
                            </div>

                            {entries.length > 1 && !isReadOnly && (
                                <button 
                                    className="btn-remove-entry" 
                                    onClick={() => removeEntry(entry.id)} 
                                    title="Eintrag löschen"
                                >
                                    🗑️
                                </button>
                            )}
                        </div>
                     );
                })
            ) : (
                <div className="undated-info-box" style={{ padding: '20px', textAlign: 'center', background: '#f1f5f9', borderRadius: '8px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '0.85rem' }}>
                    Einsatz wird ohne Datum in der Sidebar gespeichert.
                </div>
            )}
        </div>
    );
};

export default TimeEntries;
